// ============================================================================
// AppointmentSlotPicker — выбор свободного окна (пациент/врач).
//
// Сетка строит клиент (lib/medicAppointments.buildDaySlots): рабочие часы —
// настройка пользователя, занятость — ТОЛЬКО серверные записи. Источник и
// специалист выбираются из того, что реально есть в базе (права — SSoT).
// ============================================================================

import { CalendarDays, ChevronLeft, ChevronRight, Clock3, Loader2, RefreshCw, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  slotReasonLabel,
  type AppointmentCapabilities,
  type AppointmentPerson,
  type AppointmentSlot,
  type AppointmentSource,
} from '@/lib/medicAppointments';

export interface AppointmentSlotPickerProps {
  capabilities: AppointmentCapabilities;
  specialists: AppointmentPerson[];
  source: AppointmentSource;
  onSourceChange: (source: AppointmentSource) => void;
  specialistId: string;
  onSpecialistChange: (id: string) => void;
  dateKey: string;
  onDateChange: (key: string) => void;
  durationMin: number;
  onDurationChange: (minutes: number) => void;
  slots: AppointmentSlot[];
  closed: boolean;
  loading: boolean;
  error: string;
  selectedStart: string;
  onSelectSlot: (slot: AppointmentSlot) => void;
  onOpenSettings: () => void;
  onRefresh: () => void;
  patientMode: boolean;
  /** Показывать настройку рабочих часов (пациенту — нет: SSoT расписания не его). */
  showSettings?: boolean;
}

function shiftDate(key: string, deltaDays: number): string {
  const date = new Date(`${key}T00:00:00`);
  if (Number.isNaN(date.getTime())) return key;
  date.setDate(date.getDate() + deltaDays);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function sourceLabel(source: AppointmentSource): string {
  return source === 'core' ? 'Медицина' : 'Психология';
}

export function AppointmentSlotPicker(props: AppointmentSlotPickerProps) {
  const {
    capabilities, specialists, source, onSourceChange, specialistId, onSpecialistChange,
    dateKey, onDateChange, durationMin, onDurationChange, slots, closed, loading, error,
    selectedStart, onSelectSlot, onOpenSettings, onRefresh, patientMode, showSettings = true,
  } = props;

  const sourceSpecialists = specialists.filter((person) => person.sources.includes(source));
  const bothSources = capabilities.core.available && capabilities.psy.available;
  const selectedDuration = source === 'core' ? 30 : Math.max(5, durationMin);
  const noSpecialist = !specialistId;
  // Пациент не выбирает длительность приёма; для medic_encounters её нет в данных.
  const durationLocked = source === 'core' || patientMode;

  return (
    <div className="rounded-xl border bg-card">
      <div className="flex flex-wrap items-center gap-2 border-b bg-muted/20 px-3 py-2">
        <CalendarDays className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium">Свободные окна</span>
        {bothSources && (
          <div className="flex overflow-hidden rounded-lg border">
            {(['core', 'psy'] as AppointmentSource[]).map((src) => (
              <button
                key={src}
                type="button"
                className={`px-2.5 py-1 text-[11px] ${source === src ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
                onClick={() => onSourceChange(src)}
              >
                {sourceLabel(src)}
              </button>
            ))}
          </div>
        )}
        <div className="ml-auto flex items-center gap-1">
          <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => onDateChange(shiftDate(dateKey, -1))} title="Предыдущий день">
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <input
            type="date"
            value={dateKey}
            onChange={(event) => { if (event.target.value) onDateChange(event.target.value); }}
            className="h-7 rounded-md border bg-background px-2 text-[11px]"
            aria-label="Дата записи"
          />
          <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => onDateChange(shiftDate(dateKey, 1))} title="Следующий день">
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
          {showSettings && (
            <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={onOpenSettings} title="Рабочие часы и шаг сетки">
              <Settings2 className="mr-1 h-3 w-3" />
              Сетка
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={onRefresh} title="Обновить занятость">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="grid gap-2 border-b px-3 py-2 sm:grid-cols-[minmax(0,1fr)_140px]">
        <div>
          <div className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
            {patientMode ? 'Специалист' : 'Расписание специалиста'}
          </div>
          <select
            className="h-8 w-full rounded-md border bg-background px-2 text-xs"
            value={specialistId}
            onChange={(event) => onSpecialistChange(event.target.value)}
          >
            {!patientMode && <option value="">Все специалисты</option>}
            {sourceSpecialists.length === 0 && <option value="">Нет специалистов</option>}
            {sourceSpecialists.map((person) => (
              <option key={person.id} value={person.id}>{person.name}</option>
            ))}
          </select>
        </div>
        <div>
          <div className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">Длительность</div>
          {durationLocked ? (
            <div className="flex h-8 items-center rounded-md border bg-muted/30 px-2 text-xs text-muted-foreground">
              {selectedDuration} мин (фиксировано)
            </div>
          ) : (
            <select
              className="h-8 w-full rounded-md border bg-background px-2 text-xs"
              value={selectedDuration}
              onChange={(event) => onDurationChange(Number(event.target.value))}
            >
              {[15, 30, 45, 60, 90].map((minutes) => (
                <option key={minutes} value={minutes}>{minutes} мин</option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div className="p-3">
        {error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        )}
        {!error && loading && (
          <div className="flex items-center gap-2 py-4 text-xs text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Загрузка свободных окон…
          </div>
        )}
        {!error && !loading && sourceSpecialists.length === 0 && (
          <div className="rounded-lg border px-3 py-3 text-xs text-muted-foreground">
            {patientMode
              ? 'Нет специалистов, к которым можно записаться. Врач/психолог назначается администратором базы (роль «Врач» или «Психолог»).'
              : 'Нет специалистов с медицинскими ролями. Назначьте роль «Врач»/«Психолог» пользователю в конфигураторе.'}
          </div>
        )}
        {!error && !loading && sourceSpecialists.length > 0 && noSpecialist && (
          <div className="rounded-lg border px-3 py-3 text-xs text-muted-foreground">
            Выберите специалиста — сетка свободных окон строится по его занятости.
          </div>
        )}
        {!error && !loading && sourceSpecialists.length > 0 && !noSpecialist && closed && (
          <div className="rounded-lg border px-3 py-3 text-xs text-muted-foreground">
            В этот день приём не ведётся (выходной по настройке сетки). Выберите другой день или измените рабочие дни в настройках.
          </div>
        )}
        {!error && !loading && sourceSpecialists.length > 0 && !noSpecialist && !closed && slots.length === 0 && (
          <div className="rounded-lg border px-3 py-3 text-xs text-muted-foreground">
            Свободных окон нет. Выберите другой день или другого специалиста.
          </div>
        )}
        {!error && !loading && !noSpecialist && slots.length > 0 && (
          <>
            <div className="mb-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Clock3 className="h-3 w-3" />
              Нажмите на свободное окно. Занятые окна недоступны.
            </div>
            {!slots.some((slot) => slot.available) && (
              <div className="mb-2 rounded-lg border border-amber-500/40 bg-amber-500/5 px-2.5 py-1.5 text-[11px] text-amber-800 dark:text-amber-300">
                Свободных окон нет: все заняты, в перерыве или уже прошли.
              </div>
            )}
            <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-4 md:grid-cols-6">
              {slots.map((slot) => {
                const selected = selectedStart === slot.startAt;
                return (
                  <button
                    key={slot.startAt}
                    type="button"
                    disabled={!slot.available}
                    title={slotReasonLabel(slot.reason)}
                    onClick={() => onSelectSlot(slot)}
                    className={[
                      'rounded-lg border px-2 py-1.5 text-xs transition-colors',
                      selected
                        ? 'border-primary bg-primary text-primary-foreground'
                        : slot.available
                          ? 'bg-background hover:border-primary hover:bg-primary/5'
                          : 'cursor-not-allowed bg-muted/40 text-muted-foreground line-through opacity-60',
                    ].join(' ')}
                  >
                    {slot.startAt.slice(11, 16)}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
